import * as React from 'react';
import { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { Button } from 'components/bootstrap';
import { Icon, PaginatedList, SearchForm, Spinner, NoEntitiesExist, NoSearchResult } from 'components/common';
import type View from 'views/logic/views/View';
import QueryHelper from 'components/common/QueryHelper';
import EntityDataTable from 'components/common/EntityDataTable';
import type { ColumnRenderers } from 'components/common/EntityDataTable/types';
import { Link } from 'components/common/router';
import Routes from 'routing/Routes';
import usePaginationAndTableLayout from 'hooks/usePaginationAndTableLayout';
import type { Sort } from 'stores/PaginationTypes';
import useSavedSearches from 'views/hooks/useSavedSearches';

type Props = {
  onAddSearch: (id: string) => void;
};

const StyledIcon = styled(Icon)<{ $isFavorite: boolean }>(
  ({ theme, $isFavorite }) => css`
    color: ${$isFavorite ? theme.colors.variant.info : undefined};
  `,
);

const ENTITY_TABLE_ID = 'saved-searches-for-investigations';
const DEFAULT_LAYOUT = {
  pageSize: 20,
  sort: { attributeId: 'title', direction: 'asc' } as Sort,
  displayedColumns: ['title', 'description', 'summary'],
  columnsOrder: ['title', 'summary', 'description', 'owner', 'created_at', 'favorite'],
};

const useColumnRenderers = (): ColumnRenderers<View> => ({
  attributes: {
    title: {
      renderCell: (title: string, search) => <Link to={Routes.VIEWS.VIEWID(search.id)}>{title}</Link>,
    },
    favorite: {
      renderCell: (isFavorite: boolean) => (
        <StyledIcon $isFavorite={isFavorite} name="star" type={isFavorite ? 'solid' : 'regular'} />
      ),
    },
  },
});

const SavedSearchesOverview = ({ onAddSearch }: Props) => {
  const {
    isLoadingLayoutPreferences,
    onPageChange,
    layoutConfig,
    activePage,
    onSearch,
    onResetSearch,
    searchParams,
    onColumnsChange,
    onSortChange,
    onPageSizeChange,
    columnsOrder,
  } = usePaginationAndTableLayout(ENTITY_TABLE_ID, DEFAULT_LAYOUT);

  const { data: paginatedSavedSearches, isInitialLoading: isLoadingSavedSearches } = useSavedSearches(searchParams, {
    enabled: !isLoadingLayoutPreferences,
  });

  const renderSavedSearchActions = useCallback(
    (search: View) => (
      <Button bsSize="xsmall" onClick={() => onAddSearch(search.id)}>
        Add Search
      </Button>
    ),
    [onAddSearch],
  );

  const customColumnRenderers = useColumnRenderers();

  if (isLoadingSavedSearches || isLoadingLayoutPreferences) {
    return <Spinner />;
  }

  const { list: savedSearches, pagination, attributes } = paginatedSavedSearches;

  return (
    <PaginatedList
      onChange={onPageChange}
      totalItems={pagination?.total}
      pageSize={layoutConfig.pageSize}
      activePage={activePage}
      showPageSizeSelect={false}
      useQueryParameter={false}>
      <div style={{ marginBottom: '5px' }}>
        <SearchForm
          focusAfterMount
          onSearch={onSearch}
          queryHelpComponent={<QueryHelper entityName="search" commonFields={['id', 'title']} />}
          topMargin={0}
          onReset={onResetSearch}
        />
      </div>
      {pagination?.total === 0 && !searchParams.query && (
        <NoEntitiesExist>No saved searches have been created yet.</NoEntitiesExist>
      )}
      {pagination?.total === 0 && searchParams.query && (
        <NoSearchResult>No saved searches have been found.</NoSearchResult>
      )}
      {!!savedSearches?.length && (
        <EntityDataTable<View>
          entities={savedSearches}
          visibleColumns={layoutConfig.displayedAttributes}
          columnsOrder={columnsOrder}
          entityAttributesAreCamelCase
          onColumnsChange={onColumnsChange}
          onSortChange={onSortChange}
          activeSort={layoutConfig.sort}
          pageSize={searchParams.pageSize}
          onPageSizeChange={onPageSizeChange}
          actionsCellWidth={120}
          entityActions={renderSavedSearchActions}
          columnRenderers={customColumnRenderers}
          columnDefinitions={attributes}
        />
      )}
    </PaginatedList>
  );
};

export default SavedSearchesOverview;
